import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { motion } from 'framer-motion';
import { ArrowLeft, Loader2, Search, Trash2, X } from 'lucide-react';
import { toast } from 'sonner';
import { departmentLabel, type Department, type RoutingRule } from '@refdash/api-client';
import { useAuth } from '@/lib/auth-context';
import { api } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';

/**
 * Map GlobeMed's specialties to the departments that should receive them.
 *
 * The list is 110 long and fixed by GlobeMed, so this is a *mapping* job, not
 * a "type a rule" job: the work is assigning many specialties to a handful of
 * departments once, then editing one occasionally. Hence search over the whole
 * catalogue, bulk-assign for the initial pass, and a per-row select for the
 * one-off — rather than a form that asks someone to spell "Ophtalmology&
 * Ophtalmic Surgery" correctly.
 */

type Filter = 'all' | 'mapped' | 'unmapped';

const selectClass =
  'h-9 rounded-lg border border-border bg-background px-2.5 text-sm outline-none focus:border-primary';

export function RoutingSettingsPage() {
  const { user } = useAuth();
  const hospitalId = user?.hospital_id ?? '';
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const [query, setQuery] = useState('');
  const [filter, setFilter] = useState<Filter>('all');
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [bulkTarget, setBulkTarget] = useState('');

  const { data: specialties = [], isLoading: loadingSpecs } = useQuery({
    queryKey: ['specialties'],
    queryFn: () => api.hospitals.listSpecialties(),
    staleTime: 60 * 60 * 1000, // GlobeMed's catalogue, not our data
  });
  const { data: departments = [] } = useQuery({
    queryKey: ['departments', hospitalId],
    queryFn: () => api.hospitals.listDepartments(hospitalId),
    enabled: !!hospitalId,
  });
  const { data: rules = [], isLoading: loadingRules } = useQuery({
    queryKey: ['routing', hospitalId],
    queryFn: () => api.hospitals.listRouting(hospitalId),
    enabled: !!hospitalId,
  });

  // Rules are keyed by the specialty text, matched case-insensitively server-side.
  const ruleByValue = useMemo(() => {
    const m = new Map<string, RoutingRule>();
    for (const r of rules) m.set(r.match_value.toLowerCase(), r);
    return m;
  }, [rules]);

  const refresh = () => queryClient.invalidateQueries({ queryKey: ['routing'] });

  const assign = useMutation({
    mutationFn: async ({ description, code, departmentId }: {
      description: string; code: string; departmentId: number | null;
    }) => {
      const existing = ruleByValue.get(description.toLowerCase());
      if (departmentId === null) {
        if (existing) await api.hospitals.deleteRouting(hospitalId, existing.id);
        return;
      }
      // Both: the code is what matching prefers, the text is the fallback for
      // cases filed by an agent that doesn't send codes yet.
      await api.hospitals.upsertRouting(hospitalId, {
        match_type: 'specialty', match_value: description,
        department_id: departmentId, specialty_code: code,
      });
    },
    onSuccess: refresh,
    onError: (e: Error) => toast.error(e.message),
  });

  const bulkAssign = useMutation({
    mutationFn: async (departmentId: number | null) => {
      const targets = [...selected];
      const codeFor = new Map(specialties.map((s) => [s.description, s.code]));
      for (const description of targets) {
        const existing = ruleByValue.get(description.toLowerCase());
        if (departmentId === null) {
          if (existing) await api.hospitals.deleteRouting(hospitalId, existing.id);
        } else {
          await api.hospitals.upsertRouting(hospitalId, {
            match_type: 'specialty', match_value: description,
            department_id: departmentId, specialty_code: codeFor.get(description) ?? null,
          });
        }
      }
      return targets.length;
    },
    onSuccess: (count, departmentId) => {
      const dept = departments.find((d) => d.id === departmentId);
      toast.success(
        dept ? `${count} specialt${count === 1 ? 'y' : 'ies'} → ${dept.name}`
             : `${count} specialt${count === 1 ? 'y' : 'ies'} unmapped`,
      );
      setSelected(new Set());
      setBulkTarget('');
      refresh();
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const deleteRule = useMutation({
    mutationFn: (id: number) => api.hospitals.deleteRouting(hospitalId, id),
    onSuccess: () => { toast.success('Rule removed'); refresh(); },
    onError: (e: Error) => toast.error(e.message),
  });

  const mappedCount = specialties.filter(
    (s) => ruleByValue.has(s.description.toLowerCase()),
  ).length;

  const visible = useMemo(() => {
    const q = query.trim().toLowerCase();
    return specialties.filter((s) => {
      const isMapped = ruleByValue.has(s.description.toLowerCase());
      if (filter === 'mapped' && !isMapped) return false;
      if (filter === 'unmapped' && isMapped) return false;
      if (!q) return true;
      return s.description.toLowerCase().includes(q) || s.code.includes(q);
    });
  }, [specialties, ruleByValue, filter, query]);

  // A rule someone typed by hand (or that GlobeMed has since renamed) has no
  // catalogue row to sit on. Surfacing it separately beats deleting it quietly.
  const orphanRules = useMemo(() => {
    const known = new Set(specialties.map((s) => s.description.toLowerCase()));
    return rules.filter((r) => !known.has(r.match_value.toLowerCase()));
  }, [rules, specialties]);

  const allVisibleSelected = visible.length > 0
    && visible.every((s) => selected.has(s.description));

  const toggleAllVisible = () => {
    const next = new Set(selected);
    if (allVisibleSelected) visible.forEach((s) => next.delete(s.description));
    else visible.forEach((s) => next.add(s.description));
    setSelected(next);
  };

  const toggle = (description: string) => {
    const next = new Set(selected);
    if (next.has(description)) next.delete(description);
    else next.add(description);
    setSelected(next);
  };

  if (loadingSpecs || loadingRules) {
    return (
      <div className="px-5 py-5 max-w-lg lg:max-w-3xl mx-auto space-y-3">
        <Skeleton className="h-10 w-40" />
        <Skeleton className="h-64 w-full rounded-2xl" />
      </div>
    );
  }

  return (
    <div className="px-5 py-5 pb-28 max-w-lg lg:max-w-3xl mx-auto">
      <motion.div
        initial={{ opacity: 0, y: -8 }}
        animate={{ opacity: 1, y: 0 }}
        className="flex items-center gap-3 mb-4"
      >
        <button
          onClick={() => navigate('/settings')}
          className="flex items-center gap-1.5 text-sm font-medium text-muted-foreground hover:text-foreground transition-colors cursor-pointer"
        >
          <ArrowLeft className="w-4 h-4" />
          Back
        </button>
        <h2 className="font-sans text-lg font-extrabold text-foreground leading-none">
          Case routing
        </h2>
      </motion.div>

      <p className="mb-4 text-sm text-muted-foreground">
        Cases arrive from GlobeMed carrying a specialty. Send each one to the department
        that should receive it — anything left unmapped stays in the coordinator's queue.
      </p>

      {departments.length === 0 ? (
        <Card className="border-dashed p-8 text-center">
          <p className="text-sm text-muted-foreground">
            Add a department first, then map specialties to it.
          </p>
          <Button variant="outline" size="sm" className="mt-3"
                  onClick={() => navigate('/settings/departments')}>
            Go to Departments
          </Button>
        </Card>
      ) : (
        <>
          {/* Search + filters */}
          <div className="sticky top-0 z-10 -mx-1 mb-3 bg-background/95 px-1 pb-2 pt-1 backdrop-blur">
            <div className="relative">
              <Search className="pointer-events-none absolute start-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder={`Search ${specialties.length} specialties by name or code…`}
                className="h-11 w-full rounded-xl border border-border bg-background ps-9 pe-9 text-sm outline-none focus:border-primary"
              />
              {query && (
                <button
                  onClick={() => setQuery('')}
                  aria-label="Clear search"
                  className="absolute end-2 top-1/2 -translate-y-1/2 rounded-md p-1.5 text-muted-foreground hover:bg-secondary"
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              )}
            </div>
            <div className="mt-2 flex flex-wrap items-center gap-1.5">
              {([
                ['all', `All ${specialties.length}`],
                ['mapped', `Mapped ${mappedCount}`],
                ['unmapped', `Unmapped ${specialties.length - mappedCount}`],
              ] as Array<[Filter, string]>).map(([key, label]) => (
                <button
                  key={key}
                  onClick={() => setFilter(key)}
                  className={`rounded-full px-3 py-1.5 text-xs font-semibold transition-colors ${
                    filter === key
                      ? 'bg-primary text-primary-foreground'
                      : 'bg-secondary text-muted-foreground hover:text-foreground'
                  }`}
                >
                  {label}
                </button>
              ))}
              {visible.length > 0 && (
                <button
                  onClick={toggleAllVisible}
                  className="ms-auto text-xs font-semibold text-primary hover:underline"
                >
                  {allVisibleSelected ? 'Clear selection' : `Select these ${visible.length}`}
                </button>
              )}
            </div>
          </div>

          {/* The catalogue */}
          {visible.length === 0 ? (
            <Card className="border-dashed p-8 text-center">
              <p className="text-sm text-muted-foreground">
                No specialty matches “{query}”.
              </p>
            </Card>
          ) : (
            <div className="divide-y divide-border overflow-hidden rounded-2xl border border-border">
              {visible.map((s) => {
                const rule = ruleByValue.get(s.description.toLowerCase());
                const isSelected = selected.has(s.description);
                return (
                  <label
                    key={s.code}
                    className={`flex cursor-pointer items-center gap-3 px-3 py-2.5 transition-colors ${
                      isSelected ? 'bg-primary/5' : 'bg-card hover:bg-secondary/40'
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={isSelected}
                      onChange={() => toggle(s.description)}
                      aria-label={`Select ${s.description}`}
                      className="h-4 w-4 shrink-0 accent-[var(--color-primary)]"
                    />
                    <span className="w-12 shrink-0 font-mono text-xs text-muted-foreground tabular-nums">
                      {s.code}
                    </span>
                    <span className="min-w-0 flex-1 truncate text-sm font-semibold text-foreground">
                      {s.description}
                    </span>
                    <select
                      value={rule ? String(rule.department_id) : ''}
                      onChange={(e) => assign.mutate({
                        description: s.description,
                        code: s.code,
                        departmentId: e.target.value === '' ? null : Number(e.target.value),
                      })}
                      onClick={(e) => e.preventDefault()}
                      aria-label={`Department for ${s.description}`}
                      className={`${selectClass} max-w-[9.5rem] shrink-0 ${
                        rule ? 'text-foreground' : 'text-muted-foreground'
                      }`}
                    >
                      <option value="">Unmapped</option>
                      {departments.map((d) => (
                        <option key={d.id} value={d.id}>{d.name}</option>
                      ))}
                    </select>
                  </label>
                );
              })}
            </div>
          )}

          {orphanRules.length > 0 && (
            <div className="mt-5">
              <p className="mb-1 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                Not in GlobeMed's catalogue
              </p>
              <p className="mb-2 text-xs text-muted-foreground">
                Typed by hand, or renamed by GlobeMed since. These still route cases whose
                specialty matches exactly.
              </p>
              <div className="divide-y divide-border overflow-hidden rounded-2xl border border-border">
                {orphanRules.map((r) => (
                  <div key={r.id} className="flex items-center gap-3 bg-card px-3 py-2.5">
                    <span className="min-w-0 flex-1 truncate text-sm font-semibold text-foreground">
                      {r.match_value}
                    </span>
                    <span className="truncate text-sm text-muted-foreground">
                      {r.department_name
                        ?? departments.find((d) => d.id === r.department_id)?.name
                        ?? `#${r.department_id}`}
                    </span>
                    <Button
                      variant="ghost"
                      size="icon-sm"
                      className="text-red-500 hover:bg-red-50 hover:text-red-600"
                      onClick={() => deleteRule.mutate(r.id)}
                      aria-label={`Remove rule for ${r.match_value}`}
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      )}

      {/* Bulk bar — the whole point of the checkboxes: map a search result in one go */}
      {selected.size > 0 && (
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          // lg:left-64 matches the app shell's fixed sidebar (lg:w-64) so the
          // bar lines up with the content column instead of sliding under it.
          className="fixed inset-x-0 bottom-0 z-20 border-t border-border bg-card/95 px-4 py-3 backdrop-blur mb-nav lg:left-64 lg:mb-0"
        >
          <div className="mx-auto flex max-w-lg flex-wrap items-center gap-2 lg:max-w-3xl">
            <span className="text-sm font-semibold text-foreground">
              {selected.size} selected
            </span>
            <select
              value={bulkTarget}
              onChange={(e) => setBulkTarget(e.target.value)}
              aria-label="Assign selected specialties to department"
              className={`${selectClass} ms-auto`}
            >
              <option value="">Assign to…</option>
              {departments.map((d) => (
                <option key={d.id} value={d.id}>{departmentLabel(d)}</option>
              ))}
              <option value="none">Unmapped (coordinator queue)</option>
            </select>
            <Button
              size="sm"
              disabled={!bulkTarget || bulkAssign.isPending}
              onClick={() => bulkAssign.mutate(bulkTarget === 'none' ? null : Number(bulkTarget))}
            >
              {bulkAssign.isPending
                ? <Loader2 className="h-4 w-4 animate-spin" />
                : 'Apply'}
            </Button>
            <Button variant="ghost" size="sm" onClick={() => setSelected(new Set())}>
              Cancel
            </Button>
          </div>
        </motion.div>
      )}
    </div>
  );
}
